import { useState, useMemo, useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck, faFileLines, faTimes, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons"
import { t } from "i18next"
import { SlideIn } from "@/utils/animations.utils"
import Button from "@/components/button"
import SearchBar from "@/components/search-bar"
import ListItem from "@/components/list-item"
import Checkbox from "@/components/checkbox"
import ListCell from "@/components/list-cell"
import Space from "@/components/space"
import Tooltip from "@/components/tooltip"
import { NavLink } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import unitStore from "../../../unit/model/unit.store"
import jobStore from "../../../job/model/job.store"
import { STATUS_SAVING } from "@/redux/_status.types"
import JobsChips from "../../../job/model/ui/JobsChips"

interface Props {
  onClose: () => void
}

export default function RiskInrsSideNav({ onClose }: Props) {
  const dispatch = useAppDispatch()

  const _session = useAppSelector((s: any) => s._session)
  const draft = useAppSelector(unitStore.selectors.draft)
  const status = useAppSelector(unitStore.selectors.status)
  const jobs = useAppSelector(jobStore.selectors.list)

  // Chargement jobs
  useEffect(() => {
    dispatch(jobStore.thunks.fetchAndSet())
  }, [])

  // Recherche
  const [searchValue, setSearchValue] = useState("")
  const filteredJobs = useMemo(() => {
    const q = searchValue.toLowerCase()
    return q ? jobs.filter((j) => j.jobName.toLowerCase().includes(q)) : jobs
  }, [searchValue, jobs])

  // Métier sélectionné au centre
  const [currentJobId, setCurrentJobId] = useState<string | null>(null)
  const currentJob = jobs.find((j) => j.id === currentJobId) ?? null

  // Toggle Unit ↔ Job
  const toggleJob = () => {
    if (!currentJob) return
    const jobId = currentJob.id

    const isSelected = draft.JobIds.includes(jobId)

    dispatch(
      unitStore.actions.edit({
        key: "JobIds",
        value: isSelected ? draft.JobIds.filter((x) => x !== jobId) : [...draft.JobIds, jobId]
      })
    )
  }

  const onValidate = () => onClose()

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30 py-8">
      <SlideIn isLeft={false}>
        <div className="px-[30px] pt-20 w-[95vw] h-[80vh] bg-white rounded-tl-lg rounded-bl-lg shadow mb-7 flex flex-col overflow-hidden">
          {/* HEADER */}
          <div className="flex items-center border-b text-xl font-bold gap-2 mb-20">
            <FontAwesomeIcon icon={faFileLines} color="#999" />
            {t("Rechercher une fiche métier (INRS)")}
          </div>

          {/* SELECTED JOBS */}
          <div className="flex items-center gap-3 mb-6">
            <div className="font-semibold text-primary">{t("Métiers sélectionnés")}</div>
            <JobsChips />
          </div>

          {/* CONTENT */}
          <div className="flex-1 overflow-hidden">
            <div className="flex gap-10 h-full">
              {/* LEFT LIST */}
              <div className="w-[280px] flex flex-col">
                <SearchBar searchText={searchValue} onSearch={setSearchValue} />

                <div className="mt-2 flex-1 overflow-y-auto pr-1">
                  <div className="bg-white rounded-md border shadow-sm">
                    {filteredJobs.length ? (
                      filteredJobs.map((job) => {
                        const isCurrent = currentJobId === job.id
                        const isSelected = draft.JobIds.includes(job.id)

                        return (
                          <ListItem
                            key={job.id}
                            isSmall
                            onClick={() => setCurrentJobId(job.id)}
                            className={`cursor-pointer px-3 py-2 flex items-center gap-3 rounded-sm transition
                              ${isCurrent ? "bg-primary/10" : "hover:bg-gray-100"}`}>
                            <FontAwesomeIcon
                              icon={faFileLines}
                              style={{
                                color: isSelected ? _session.accountColors.active : "#999"
                              }}
                            />
                            <span
                              style={{
                                color: isSelected ? _session.accountColors.active : "inherit"
                              }}>
                              {job.jobName}
                            </span>
                          </ListItem>
                        )
                      })
                    ) : (
                      <ListItem className="text-gray-400 py-3">{t("Aucun métier trouvé.")}</ListItem>
                    )}
                  </div>
                </div>
              </div>

              {/* CENTER PANEL */}
              <div className="flex-1 min-w-[420px] flex flex-col overflow-hidden">
                {!currentJob && (
                  <p className="text-gray-400 text-center">{t("Sélectionnez un métier pour afficher les risques.")}</p>
                )}

                {currentJob && (
                  <div className="h-full px-[30px] py-20 border rounded-lg bg-gray-50 shadow-inner flex flex-col overflow-hidden">
                    {/* JOB HEADER */}
                    <div className="flex items-center gap-3 mb-4">
                      <ListCell>
                        <Checkbox active={draft.JobIds.includes(currentJob.id)} onClick={toggleJob} />
                      </ListCell>

                      <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                        <FontAwesomeIcon icon={faFileLines} color="#999" />
                        {currentJob.jobName}
                      </h3>

                      <Space />

                      <Tooltip
                        text={t(`Consultez la fiche métier sur le site INRS (${currentJob.url})`)}
                        position="left"
                        width={300}>
                        <NavLink to={currentJob.url} target="_blank" className="!text-gray-400 !underline">
                          {t("Référence INRS")}
                        </NavLink>
                      </Tooltip>
                    </div>

                    {/* RISKS LIST */}
                    <div className="space-y-4 overflow-y-auto pr-2" style={{ maxHeight: "calc(85vh - 300px)" }}>
                      {currentJob.risks?.map((risk, i) => (
                        <div key={i} className="p-4 border rounded-md bg-white shadow-sm space-y-2">
                          <div className="flex items-center gap-4">
                            <FontAwesomeIcon icon={faTriangleExclamation} className="grey-t" />
                            <span className="font-medium">{risk.title}</span>
                          </div>

                          <ul className="list-disc ml-6 text-sm text-gray-700">
                            {risk.origins?.map((o, j) => (
                              <li key={j}>{o}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="sticky bottom-0 bg-white border-t p-6 flex justify-end">
            <Button onClick={onClose} icon={faTimes} isWithBorder>
              {t("utils_close")}
            </Button>
            <Button icon={faCheck} className="primary" isLoading={status === STATUS_SAVING} onClick={onValidate}>
              {t("Valider")}
            </Button>
          </div>
        </div>
      </SlideIn>
    </div>
  )
}
